import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    /** @type {import('./product-card').ProductCard | null} */
    const productCard = this.closest('product-card');

    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    // If variant is already in the URL, just return it
    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    // Otherwise, inject currently selected variant
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card.
   * @returns {string | null} The variant ID or null.
   */
  #getSelectedVariantId() {
    /** @type {import('./product-card').ProductCard | null} */
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click.
   * @param {Event} event - The click event.
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');

        if (gridElement) {
          // Cache a clone to keep original DOM detached
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Always work with a fresh clone from cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Keep the quick-add trigger visible while the dialog is open.
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content.
   * @param {string} productPageUrl - URL of the product page to fetch.
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // Abort any previous pending request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick-add modal content with the given product grid.
   * @param {Element} productGrid - The product grid element.
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent =
        productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      // Build mobile header (title + price)
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal.
   * @param {Element} modalContent - The modal content element.
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll(
      'input[type="radio"][data-variant-id]',
    );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Updates the product title links when the variant changes.
   * @param {CustomEvent} event
   */
  #updateProductTitleLink = (event) => {
    /** @type {HTMLAnchorElement | null} */
    const anchorElement = event.detail.data.html?.querySelector(
      '.view-product-title a',
    );

    /** @type {HTMLAnchorElement | null} */
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');

    /** @type {HTMLAnchorElement | null} */
    const mobileProductTitle = this.querySelector('.product-header a');

    if (!anchorElement) return;

    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href = anchorElement.href;
    }

    if (mobileProductTitle) {
      mobileProductTitle.href = anchorElement.href;
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    /**
     * Patch for an iOS bug where the UI can freeze when closing the dialog.
     * Affects iOS 16.0–16.3 specifically.
     */
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    ) {
      return;
    }

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (!grid) return;

      const currentWidth = grid.getBoundingClientRect().width;
      grid.style.width = `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
